/**
 * Resolves the course id the loading overlay should brand itself for, from the
 * URL alone.
 *
 * The overlay mounts above the router (see `LoadingProvider`), so no route
 * params are available — everything is derived from the path/query, the same
 * way `LoadingComponent`'s `isIde` check reads the path. Resolution is
 * most-specific-first:
 *
 * 1. `?apply=<course>` — a task launched from an application checklist carries
 *    the course being applied to here (the section-teaching routes and the
 *    lesson viewer; see `withApplicationTaskParams` in `apputils`).
 * 2. Any path segment that exactly equals a registered override course id
 *    (`overrideCourseIds`) — i.e. the course id itself, wherever it sits in the
 *    path. This is deliberately scheme-agnostic so route changes don't require
 *    a change here: it covers the plain course route (`/cipx/…`), the current
 *    application flow (`/{parent}/{join|applyteach}/cipx`), and any future
 *    shape that carries the course as a segment (e.g.
 *    `/apply/cipx/{student|sl}`). Only ids in the (small) override allowlist
 *    are matched, and matching is exact, so it can't misfire for ordinary
 *    courses or for variant ids like `cipx-print`.
 * 3. `/{course}/learn/<lessonId>/…` whose lessonId contains an override course
 *    id — a course's lessons can be hosted under a *parent* course (e.g. cipx
 *    lessons live in `lessons/public/lessonsList`, so the URL course is
 *    `public`), leaving the lesson id as the only signal. Naming-convention
 *    match, not a guarantee: a generic lesson reused by that course (no course
 *    id in its lesson id) falls back to the hosting course.
 * 4. Otherwise the first path segment — the plain course route.
 *
 * @param params - The resolution inputs.
 * @param params.pathname - `window.location.pathname`.
 * @param params.search - `window.location.search` (the leading `?` is
 *   optional; an empty string is treated as no query).
 * @param params.overrideCourseIds - Course ids that have a registered override
 *   (the keys of `AppOverrides.courseOverrides`). Rules 2 and 3 match against
 *   these. When omitted, only rules 1 and 4 apply.
 * @returns The effective course id, or `''` when none can be determined (e.g.
 *   the index route). Callers treat `''` as "no course override".
 * @throws Never.
 */
export function resolveLoadingCourseId({
  pathname,
  search,
  overrideCourseIds = [],
}: {
  pathname: string;
  search: string;
  overrideCourseIds?: readonly string[];
}): string {
  const applyCourse = new URLSearchParams(search).get('apply');
  if (applyCourse) {
    return applyCourse;
  }

  // Drop the leading empty segment from the absolute path: `/a/b` -> ['a','b'].
  const segments = pathname.split('/').filter(Boolean);

  // An override course appearing anywhere as a path segment wins — matched by
  // the course id itself, so this stays correct across application route
  // schemes without a change here (see rule 2 in the doc comment).
  const overrideSegment = segments.find(seg => overrideCourseIds.includes(seg));
  if (overrideSegment) {
    return overrideSegment;
  }

  // A course's lessons can be hosted under a parent course, so the URL course
  // is the parent and the lesson id is the only signal (convention-based).
  const [first, second, third] = segments;
  if (second === 'learn' && third) {
    const lessonCourse = overrideCourseIds.find(id => third.includes(id));
    if (lessonCourse) {
      return lessonCourse;
    }
  }

  return first ?? '';
}
